/**
 * Greptile-style left sidebar. Five groups (Repositories / Agent Config /
 * Features / Admin / Developer) with the active item highlighted by
 * `react-router-dom`'s `NavLink` (which already manages the `aria-current`
 * and `.active` class for us).
 *
 * Chrome features (Phase 8 — Greptile-parity v2):
 *
 * - **Search filter** — a `Search settings…` input at the top filters the
 *   visible entries (label substring, case-insensitive). Hidden groups
 *   collapse entirely when no entries match.
 * - **Expandable Code Review submenu** — `Agent Config → Code Review`
 *   becomes a parent row with a chevron. Clicking the row toggles
 *   the nested list; the open/closed state is persisted to
 *   `localStorage` so the menu stays where you left it across reloads.
 * - **Group separators** — each `.feluda-sidebar-group` after the first
 *   gets a `1px` top border so they read as distinct sections.
 *
 * `SettingsForm` (used by `/code-review/*` sub-pages) lives in
 * `components/SettingsForm.tsx` and reads from the same
 * `useSettings()` hook as the legacy `/settings` page.
 */
import { useEffect, useRef, useState, type ReactNode } from 'react';
import { NavLink } from 'react-router-dom';

type SidebarChild = {
  to: string;
  icon: string;
  label: string;
  badge?: string;
  disabled?: boolean;
};

interface SidebarLinkProps {
  to: string;
  icon: string;
  label: string;
  badge?: string;
  disabled?: boolean;
}

function SidebarLink({ to, icon, label, badge, disabled }: SidebarLinkProps) {
  const content = (
    <>
      <span className="feluda-sidebar-link-icon" aria-hidden="true">{icon}</span>
      <span className="feluda-sidebar-link-label">{label}</span>
      {badge && <span className="feluda-sidebar-link-badge">{badge}</span>}
    </>
  );
  if (disabled) {
    return (
      <span
        className="feluda-sidebar-link feluda-sidebar-link-disabled"
        aria-disabled="true"
      >
        {content}
      </span>
    );
  }
  return (
    <NavLink
      to={to}
      className={({ isActive }) =>
        `feluda-sidebar-link${isActive ? ' feluda-sidebar-link-active' : ''}`
      }
    >
      {content}
    </NavLink>
  );
}

interface SidebarGroupProps {
  title: string;
  children: ReactNode;
}

function SidebarGroup({ title, children }: SidebarGroupProps) {
  return (
    <div className="feluda-sidebar-group">
      <h3 className="feluda-sidebar-group-title">{title}</h3>
      <ul className="feluda-sidebar-group-list">{children}</ul>
    </div>
  );
}

interface SidebarState {
  query: string;
  expanded: Record<string, boolean>;
}

const STORAGE_KEY = 'feluda.sidebar.v1';

function loadSidebarState(): SidebarState {
  // jsdom and bare test environments strip `window.localStorage`; guard.
  if (typeof window === 'undefined') return { query: '', expanded: {} };
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return { query: '', expanded: {} };
    const parsed = JSON.parse(raw) as Partial<SidebarState>;
    return {
      query: typeof parsed.query === 'string' ? parsed.query : '',
      expanded:
        parsed.expanded && typeof parsed.expanded === 'object'
          ? (parsed.expanded as Record<string, boolean>)
          : {},
    };
  } catch {
    return { query: '', expanded: {} };
  }
}

function persistSidebarState(next: SidebarState) {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
  } catch {
    // localStorage quota / disabled — drop the write silently.
  }
}

interface ParentEntry extends SidebarChild {
  children: SidebarChild[];
}

interface SidebarEntryGroup {
  title: string;
  entries: Array<SidebarChild | ParentEntry>;
}

function isParent(entry: SidebarChild | ParentEntry): entry is ParentEntry {
  return Array.isArray((entry as ParentEntry).children);
}

function matchQuery(label: string, query: string): boolean {
  if (!query) return true;
  return label.toLowerCase().includes(query.trim().toLowerCase());
}

function visibleEntries<T extends SidebarChild>(entries: T[], query: string): T[] {
  if (!query.trim()) return entries;
  return entries.filter((e) => matchQuery(e.label, query));
}

export function Sidebar() {
  const initial = useRef(loadSidebarState());
  const [query, setQuery] = useState(initial.current.query);
  const [expanded, setExpanded] = useState<Record<string, boolean>>(
    initial.current.expanded,
  );

  useEffect(() => {
    persistSidebarState({ query, expanded });
  }, [query, expanded]);

  const groups: SidebarEntryGroup[] = [
    {
      title: 'Repositories',
      entries: [
        { to: '/repositories', icon: '📦', label: 'Add/Remove Repos' },
        { to: '/settings', icon: '⚙️', label: 'Settings' },
        { to: '/code-providers', icon: '☁️', label: 'Code Providers', disabled: true },
      ],
    },
    {
      title: 'Agent Config',
      entries: [
        {
          to: '/code-review/when',
          icon: '🔍',
          label: 'Code Review',
          children: [
            { to: '/code-review/when', icon: '›', label: 'When Feluda Reviews' },
            { to: '/code-review/summaries', icon: '›', label: 'PR Summaries' },
            { to: '/code-review/instructions', icon: '›', label: 'Custom Instructions' },
            { to: '/code-review/comments', icon: '›', label: 'Feluda Comments' },
            {
              to: '/code-review/agents',
              icon: '›',
              label: 'Default Coding Agents',
              disabled: true,
            },
            {
              to: '/code-review/status-checks',
              icon: '›',
              label: 'Status Checks',
              disabled: true,
            },
            { to: '/code-review/auto-approve', icon: '›', label: 'Auto-approve PRs' },
          ],
        },
        { to: '/trex', icon: '⚡', label: 'TREX', badge: 'Beta', disabled: true },
      ],
    },
    {
      title: 'Features',
      entries: [{ to: '/memory', icon: '🧠', label: 'Memory', disabled: true }],
    },
    {
      title: 'Admin',
      entries: [
        { to: '/org', icon: '🏢', label: 'Organization', disabled: true },
        { to: '/people', icon: '👥', label: 'People', disabled: true },
        { to: '/billing', icon: '💳', label: 'Billing', disabled: true },
        { to: '/usage', icon: '📊', label: 'Usage', disabled: true },
      ],
    },
    {
      title: 'Developer',
      entries: [
        { to: '/api-keys', icon: '🔑', label: 'API Keys', disabled: true },
        { to: '/audit-log', icon: '📜', label: 'Audit Log', badge: 'Beta', disabled: true },
      ],
    },
  ];

  const showEmptyState = query.trim().length > 0;

  return (
    <aside className="feluda-sidebar" aria-label="Primary navigation">
      <header className="feluda-sidebar-header">
        <span className="feluda-sidebar-org">poridhi</span>
        <span className="feluda-sidebar-org-badge">Admin</span>
      </header>

      <div className="feluda-sidebar-search-wrap">
        <input
          type="search"
          className="feluda-sidebar-search"
          placeholder="Search settings…"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          aria-label="Filter sidebar entries"
          data-testid="sidebar-search"
        />
      </div>

      {groups.map((group) => {
        // An entry survives filtering if its own label matches OR, for
        // parent entries, any of its children match. We pass the full
        // <ParentEntry> to the renderer so the children can render
        // their own filtered sublist.
        const filtered = group.entries.filter((entry) => {
          if (isParent(entry)) {
            return (
              matchQuery(entry.label, query) ||
              visibleEntries(entry.children, query).length > 0
            );
          }
          return matchQuery(entry.label, query);
        });
        if (filtered.length === 0) return null;
        return (
          <SidebarGroup key={group.title} title={group.title}>
            {filtered.map((entry) =>
              isParent(entry) ? (
                <ParentSubmenu
                  key={entry.to}
                  entry={entry}
                  query={query}
                  // While the user is filtering we always force the
                  // submenu open so they can see the matched child.
                  // Once the query is empty, fall back to the persisted
                  // open/closed state.
                  isOpen={query.trim().length > 0 || !!expanded[entry.label]}
                  onToggle={() =>
                    setExpanded((e) => ({ ...e, [entry.label]: !e[entry.label] }))
                  }
                />
              ) : (
                <li key={entry.to}>
                  <SidebarLink
                    to={entry.to}
                    icon={entry.icon}
                    label={entry.label}
                    badge={entry.badge}
                    disabled={entry.disabled}
                  />
                </li>
              ),
            )}
          </SidebarGroup>
        );
      })}

      {showEmptyState && groups.every((g) => {
        return g.entries.every((entry) => {
          if (isParent(entry)) {
            return (
              !matchQuery(entry.label, query) &&
              visibleEntries(entry.children, query).length === 0
            );
          }
          return !matchQuery(entry.label, query);
        });
      }) && (
        <p className="feluda-sidebar-empty">No matches.</p>
      )}
    </aside>
  );
}

function ParentSubmenu({
  entry,
  query,
  isOpen,
  onToggle,
}: {
  entry: ParentEntry;
  query: string;
  isOpen: boolean;
  onToggle: () => void;
}) {
  const filteredChildren = visibleEntries(entry.children, query);
  return (
    <li className="feluda-sidebar-parent">
      <div className="feluda-sidebar-parent-row">
        <NavLink
          to={entry.to}
          className={({ isActive }) =>
            `feluda-sidebar-link${isActive ? ' feluda-sidebar-link-active' : ''}`
          }
        >
          <span className="feluda-sidebar-link-icon" aria-hidden="true">{entry.icon}</span>
          <span className="feluda-sidebar-link-label">{entry.label}</span>
        </NavLink>
        <button
          type="button"
          className={`feluda-sidebar-chevron${isOpen ? ' feluda-sidebar-chevron-open' : ''}`}
          aria-expanded={isOpen}
          aria-label={isOpen ? `Collapse ${entry.label}` : `Expand ${entry.label}`}
          onClick={onToggle}
        >
          <span aria-hidden="true">›</span>
        </button>
      </div>
      {isOpen && (
        <ul className="feluda-sidebar-children" data-testid={`children-${entry.label}`}>
          {filteredChildren.map((child) => (
            <li key={child.to}>
              <SidebarLink
                to={child.to}
                icon={child.icon}
                label={child.label}
                disabled={child.disabled}
              />
            </li>
          ))}
        </ul>
      )}
    </li>
  );
}
